/**
 * The draft board — the highest-priority view (§7.2, FR-6 – FR-10).
 *
 * Two lists over the same data: the full ranking with drafted players struck
 * through, and "Best Available" filtered to the undrafted. Best Available is
 * the default working view during a draft, because that is the question anyone
 * actually has while on the clock.
 *
 * Rendering goes through `reconcile` so rows are moved rather than rebuilt: the
 * board repaints every poll and must not reshuffle under a thumb (FR-39).
 */

import { el, reconcile, text, clear, fmt } from './dom.ts';
import type { AppState, Store } from '../state/store.ts';
import type { AdpRow, Player, PlayerAvailabilityDetail, PlayerId, Position, Valuation } from '../core/types.ts';
import { FANTASY_POSITIONS } from '../core/types.ts';
import type { PlayerRankView } from '../core/blend.ts';
import type { DynastyValuation } from '../core/dynasty.ts';
import type { BlueprintValuation } from '../core/blueprint.ts';
import { demandForPosition, opponentDemandForState } from './opponentDemand.ts';
import type { OpponentDemandForecast, OpponentDemandPosition } from '../core/opponentDemand.ts';

export interface BoardRow {
  player: Player;
  rank: number;
  positionRank: number | null;
  tier: number | null;
  view: PlayerRankView | undefined;
  valuation: Valuation | undefined;
  adp: AdpRow | undefined;
  drafted: { pick_no: number | null; team_name: string | null; manual?: boolean } | null;
  /** Multi-year value, when the model can price this player (§12). */
  dynasty: DynastyValuation | undefined;
  /** True when the dynasty board is active, which changes what a row shows. */
  dynastyMode: boolean;
  /** Research forecast valuation, when Blueprint is active. */
  blueprint: BlueprintValuation | undefined;
  /** True when Blueprint is active, which changes the score column. */
  blueprintMode: boolean;
  /** Tier palette band, with 9 representing tier 9 and later. */
  tierBand: number | null;
  /** Existing roster players who share this player's position and bye week. */
  byeConflict: { week: number; playerNames: string[] } | null;
  /** ADP relative to the user's next own pick. Positive means value. */
  adpDelta: number | null;
  adpSignal: BoardSignal | null;
  /** VOR or Blueprint Gain relative to replacement. */
  valueSignal: BoardSignal | null;
  /** Display-only demand pressure for this player's position before our turn. */
  opponentDemand: OpponentDemandPosition | null;
  availability: PlayerAvailabilityDetail | null;
}

export type BoardSignal = 'good' | 'neutral' | 'bad';

/** Plain-language note on how a dynasty number was reached, for the tooltip. */
function dynastyBasisLabel(v: DynastyValuation): string {
  switch (v.basis) {
    case 'draft-capital':
      return 'Incoming rookie — priced from where he went in the NFL draft, averaged over every player taken there including the ones who never played.';
    case 'age-adjusted':
      return "This season's projection carried forward and decayed by the aging curve for his position.";
    case 'no-age':
      return "Age unknown, so this season's projection is carried forward undecayed — treat it as softer than the other numbers.";
  }
}

export function buildBoardRows(state: AppState, opponentDemand: OpponentDemandForecast | null = opponentDemandForState(state)): BoardRow[] {
  const { universe, blend, engine, blueprint, draft, draftContext, adpIndex, config, dynastyValues } = state;
  const dynastyMode = config.active_source_id === 'dynasty';
  const blueprintMode = config.active_source_id === 'blueprint';
  if (!universe || !blend || !draft) return [];

  const draftedBy = new Map<PlayerId, { pick_no: number | null; team_name: string | null; manual?: boolean }>();
  for (const pick of draft.picks) {
    if (state.availabilityOverrides.has(pick.player_id)) continue;
    draftedBy.set(pick.player_id, {
      pick_no: pick.pick_no,
      team_name: pick.team_name,
      manual: pick.manual,
    });
  }

  // A dynasty rookie draft starts with every keeper already owned — those
  // players never show up in `draft.picks` (they were never picked in *this*
  // draft event) but must not read as available either. `all_rosters` carries
  // both; anything left over here is a pre-existing keeper.
  for (const [rosterId, players] of Object.entries(draft.all_rosters)) {
    const team_name = draftContext?.rosterNames.get(Number(rosterId)) ?? null;
    for (const playerId of players) {
      if (state.availabilityOverrides.has(playerId)) continue;
      if (!draftedBy.has(playerId)) draftedBy.set(playerId, { pick_no: null, team_name });
    }
  }

  const positionCounters = new Map<Position, number>();
  const rows: BoardRow[] = [];
  const adpTargetPick = draft.my_next_pick_no ?? draft.current_pick_no;
  const adpRoundThreshold = Math.max(3, Math.ceil((state.league?.teams ?? 12) / 2));
  blend.order.forEach((id, i) => {
    const player = universe.get(id);
    if (!player) return;

    let positionRank: number | null = null;
    if (player.position) {
      positionRank = (positionCounters.get(player.position) ?? 0) + 1;
      positionCounters.set(player.position, positionRank);
    }

    const drafted = draftedBy.get(id) ?? null;
    const view = blend.byPlayer.get(id);
    const byeConflict = findByeConflict(player, draft.my_roster, universe);
    const adpDelta = adpIndex.get(id) ? adpTargetPick - adpIndex.get(id)!.adp : null;
    const adpSignal = signalFromDelta(adpDelta, adpRoundThreshold);
    const displayedValue = blueprintMode
      ? blueprint?.valuations.get(id)?.marginal_lineup_value ?? null
      : dynastyMode
        ? null
        : engine?.valuations.get(id)?.vor ?? null;
    const activeTier = blueprintMode
      ? blueprint?.tiers.get(id) ?? null
      : engine?.tiers.get(id) ?? view?.ranks.find((r) => r.tier !== null)?.tier ?? null;

    rows.push({
      dynasty: dynastyValues.get(id),
      dynastyMode,
      blueprint: blueprint?.valuations.get(id),
      blueprintMode,
      player,
      rank: i + 1,
      positionRank,
      tier: activeTier,
      view,
      valuation: engine?.valuations.get(id),
      adp: adpIndex.get(id),
      drafted,
      tierBand: rowTierBand(activeTier),
      byeConflict,
      adpDelta,
      adpSignal,
      valueSignal: signalFromValue(displayedValue),
      opponentDemand: demandForPosition(opponentDemand, player.position),
      availability: state.playerAvailability.get(id) ?? null,
    });
  });

  // FR-9: filters. Applied after ranking so positional ranks stay absolute —
  // filtering to RB must not renumber everyone as RB1, RB2.
  const { position_filter, hide_below_rank, show_drafted } = config.ui;
  const currentSeasonOnly = state.league?.league_type !== 'dynasty';
  return rows.filter((row) => {
    if (position_filter && row.player.position !== position_filter) return false;
    if (hide_below_rank !== null && row.rank > hide_below_rank) return false;
    if (!show_drafted && (row.drafted || (currentSeasonOnly && row.availability?.status === 'season_out'))) return false;
    return true;
  });
}

export interface BoardCallbacks {
  onSelect: (playerId: PlayerId) => void;
  onToggleDrafted: (playerId: PlayerId, drafted: boolean) => void;
}

function injuryTitle(row: BoardRow): string {
  const details = row.availability?.reason ?? row.player.injury_status ?? 'Injury information available';
  const source = row.availability?.source_name ? ` Source: ${row.availability.source_name}.` : '';
  return `${row.availability?.status === 'season_out' ? 'Out for season. ' : ''}${details}.${source}`;
}

export function renderBoard(
  container: HTMLElement,
  state: AppState,
  rows: BoardRow[],
  callbacks: BoardCallbacks,
): void {
  const compact = state.config.ui.compact;
  container.classList.toggle('compact', compact);

  reconcile(
    container,
    rows,
    (row) => row.player.player_id_sleeper,
    (row, existing) => renderRow(row, existing, compact, callbacks),
  );
}

function renderRow(
  row: BoardRow,
  existing: HTMLElement | null,
  compact: boolean,
  callbacks: BoardCallbacks,
): HTMLElement {
  const node = existing ?? el('div', { class: 'board-row', role: 'row', tabindex: '0' });
  clear(node);

  const id = row.player.player_id_sleeper;
  node.className = [
    'board-row',
    row.drafted ? 'drafted' : '',
    row.player.position ? `pos-${row.player.position.toLowerCase()}` : '',
    row.tierBand !== null ? `tier-band-${row.tierBand}` : '',
    row.byeConflict ? 'bye-conflict' : '',
    row.availability?.status === 'season_out' ? 'season-out' : '',
  ]
    .filter(Boolean)
    .join(' ');

  node.onclick = () => callbacks.onSelect(id);
  node.onkeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callbacks.onSelect(id);
    }
    // FR-41: mark drafted is one of the three actions under time pressure.
    if (event.key.toLowerCase() === 'd') {
      event.preventDefault();
      callbacks.onToggleDrafted(id, !row.drafted);
    }
  };

  node.appendChild(el('span', { class: 'cell rank' }, String(row.rank)));

  const nameCell = el('span', { class: 'cell name' });
  nameCell.appendChild(el('span', { class: 'player-name' }, row.player.full_name));
  if (row.availability?.status === 'season_out') {
    nameCell.appendChild(
      el('span', {
        class: 'badge injury season-out-badge',
        title: injuryTitle(row),
        'aria-label': 'Out for season',
      }, 'OUT'),
    );
  } else if (row.availability?.status === 'injured' || row.player.injury_status) {
    nameCell.appendChild(
      el('span', {
        class: 'badge injury',
        title: injuryTitle(row),
      }, row.player.injury_status ?? 'INJ'),
    );
  }
  node.appendChild(nameCell);

  // Age lives with the other per-player facts rather than beside the name.
  // Badges in the name cell were tried and abandoned: the board column is
  // narrow, `.cell` clips its overflow, and anything sharing that cell either
  // gets cut off or wins the space and truncates the name to "O…" — which is
  // the one thing on a row that must always be readable.
  const dynastyAge = row.dynastyMode ? row.dynasty?.age ?? row.player.age : null;
  node.appendChild(
    (() => {
      const meta = el(
        'span',
        { class: 'cell meta' },
        el('span', { class: 'pos-tag' }, row.player.position ?? '—'),
        row.positionRank ? el('span', { class: 'pos-rank' }, String(row.positionRank)) : null,
        el('span', { class: 'team' }, row.player.team ?? 'FA'),
        row.tier !== null
          ? el(
              'span',
              {
                class: `tier-chip tier-chip-inline tier-${row.tierBand ?? 9}`,
                title: `Tier ${row.tier}`,
                'aria-label': `Tier ${row.tier}`,
              },
              `T${row.tier}`,
            )
          : null,
        dynastyAge !== null && dynastyAge !== undefined
          ? el('span', { class: 'age-tag', title: `Age ${dynastyAge}` }, `${dynastyAge}y`)
          : null,
      );
      if (row.byeConflict) {
        meta.appendChild(
          el(
            'span',
            {
              class: 'mobile-bye-conflict',
              title: byeConflictTitle(row.byeConflict),
              'aria-label': byeConflictTitle(row.byeConflict),
            },
            `B${row.byeConflict.week}!`,
          ),
        );
      }
      if (row.opponentDemand?.projected_picks !== null && row.opponentDemand?.projected_picks !== undefined && row.opponentDemand.pressure) {
        meta.appendChild(
          el(
            'span',
            {
              class: `demand-tag pressure-${row.opponentDemand.pressure}`,
              title: `${row.opponentDemand.position} demand before pick window: ${row.opponentDemand.projected_picks} projected opponent pick${row.opponentDemand.projected_picks === 1 ? '' : 's'} from ${row.opponentDemand.teams_needing} team${row.opponentDemand.teams_needing === 1 ? '' : 's'}`,
              'aria-label': `${row.opponentDemand.pressure} opponent ${row.opponentDemand.position} demand before your next pick`,
            },
            `D${row.opponentDemand.pressure[0]!.toUpperCase()}`,
          ),
        );
      }
      return meta;
    })(),
  );

  // The visual column headers live outside this reconciled container and are
  // aria-hidden, so each cell carries its own label — otherwise a screen
  // reader reads a row as a bare run of numbers.
  if (!compact) {
    node.appendChild(
      el(
        'span',
        {
          class: ['cell', 'tier', row.tierBand !== null ? `tier-band-${row.tierBand}` : ''].filter(Boolean).join(' '),
          'aria-label': row.tier !== null ? `Tier ${row.tier}` : 'No tier',
          title: row.tier !== null ? `Tier ${row.tier}` : 'No tier',
        },
        row.tier !== null
          ? el('span', { class: `tier-chip tier-${row.tierBand ?? 9}` }, `T${row.tier}`)
          : text('—'),
      ),
    );
    node.appendChild(
      el(
        'span',
        {
          class: ['cell', 'bye', row.byeConflict ? 'signal-bad' : ''].filter(Boolean).join(' '),
          'aria-label': byeLabel(row),
          title: row.byeConflict ? byeConflictTitle(row.byeConflict) : byeLabel(row),
        },
        row.player.bye_week ? `B${row.player.bye_week}${row.byeConflict ? '!' : ''}` : '—',
      ),
    );
  }

  // FR-8: value delta — how far a player has fallen relative to his ADP.
  //
  // On a dynasty board this column carries NFL draft capital instead. Fantasy
  // ADP is close to meaningless in a rookie draft — the public drafts it is
  // averaged over are redraft leagues that mostly do not contain these players
  // at all — whereas where a rookie went in the NFL draft is the single most
  // useful number on his row, and it needs somewhere to live.
  const capital = row.dynastyMode ? row.dynasty?.capital ?? null : null;
  if (row.dynastyMode) {
    const label = capital ? `${capital.round}.${String(capital.pick).padStart(2, '0')}` : '—';
    node.appendChild(
      el(
        'span',
        {
          class: 'cell adp',
          title: capital
            ? `Taken at pick ${capital.pick} (round ${capital.round}) of the ${capital.season} NFL draft`
            : 'Not in the shipped rookie draft-capital table — priced on age and ranking alone',
          'aria-label': capital
            ? `N F L draft round ${capital.round} pick ${capital.pick}`
            : 'No NFL draft capital',
        },
        label,
      ),
    );
  } else {
    const adpCell = el('span', {
      class: ['cell', 'adp', signalClass(row.adpSignal)].filter(Boolean).join(' '),
      'aria-label': row.adp ? `Average draft position ${row.adp.adp.toFixed(1)}` : 'No average draft position',
      title: row.adp ? adpTitle(row) : 'No ADP for this player',
    });
    if (row.adp) {
      adpCell.appendChild(el('span', { class: 'adp-value' }, row.adp.adp.toFixed(1)));
    } else {
      adpCell.appendChild(text('—'));
    }
    node.appendChild(adpCell);
  }

  if (!compact) {
    if (row.dynastyMode) {
      const dyn = row.dynasty;
      node.appendChild(
        el(
          'span',
          {
            class: ['cell', 'vor', dyn ? `dyn-${dyn.basis}` : '', signalClass(row.valueSignal)].filter(Boolean).join(' '),
            title: dyn ? dynastyBasisLabel(dyn) : 'The dynasty model does not price this position',
            'aria-label': dyn
              ? `Dynasty value ${Math.round(dyn.dynasty_points)}`
              : 'No dynasty value',
          },
          dyn ? String(Math.round(dyn.dynasty_points)) : '—',
        ),
      );
    } else if (row.blueprintMode) {
      const forecast = row.blueprint;
      node.appendChild(
        el(
          'span',
          {
            class: ['cell', 'vor', 'blueprint-value', signalClass(row.valueSignal)].filter(Boolean).join(' '),
            title: forecast ? valueTitle('Gain', forecast.marginal_lineup_value) : 'No Blueprint forecast for this player',
            'aria-label': forecast
              ? `Blueprint marginal lineup gain ${fmt.signed(forecast.marginal_lineup_value, 1)}`
              : 'No Blueprint lineup gain',
          },
          forecast ? fmt.signed(forecast.marginal_lineup_value, 0) : '—',
        ),
      );
    } else {
      const vor = row.valuation?.vor;
      node.appendChild(
        el(
          'span',
          {
            class: ['cell', 'vor', signalClass(row.valueSignal)].filter(Boolean).join(' '),
            title: vor === undefined ? 'No value over replacement' : valueTitle('VOR', vor),
            'aria-label': vor === undefined ? 'No value over replacement' : `Value over replacement ${fmt.signed(vor, 0)}`,
          },
          vor === undefined ? '—' : fmt.signed(vor, 0),
        ),
      );
    }
  }

  if (row.drafted) {
    const label =
      row.drafted.pick_no !== null
        ? `#${row.drafted.pick_no} ${row.drafted.team_name ?? ''}${row.drafted.manual ? ' (manual)' : ''}`
        : `Rostered — ${row.drafted.team_name ?? 'another team'}`;
    node.appendChild(el('span', { class: 'cell drafted-by' }, label));
  } else {
    node.appendChild(el('span', { class: 'cell drafted-by' }));
  }

  return node;
}

// ---------------------------------------------------------------------------
// Tier-break detection (FR-10)
// ---------------------------------------------------------------------------

export interface TierAlert {
  position: Position;
  remaining: number;
  tier: number;
  urgent: boolean;
  target_pick_no: number;
  best_survival_probability: number;
  current_players: TierAlertPlayer[];
  next_player: TierAlertPlayer | null;
  projected_point_delta: number | null;
  message: string;
}

function byeLabel(row: BoardRow): string {
  if (!row.player.bye_week) return 'Bye week unknown';
  return row.byeConflict
    ? `Bye week ${row.player.bye_week}; conflicts with ${row.byeConflict.playerNames.join(', ')}`
    : `Bye week ${row.player.bye_week}`;
}

function byeConflictTitle(conflict: NonNullable<BoardRow['byeConflict']>): string {
  return `Bye week ${conflict.week} conflicts with ${conflict.playerNames.join(', ')}`;
}

function adpTitle(row: BoardRow): string {
  if (!row.adp || row.adpDelta === null) return 'Average draft position across public drafts of this size and scoring';
  if (row.adpDelta > 0) return `${row.adpDelta.toFixed(1)} picks after ADP — value at your next pick`;
  if (row.adpDelta < 0) return `${Math.abs(row.adpDelta).toFixed(1)} picks before ADP — reach at your next pick`;
  return 'At ADP at your next pick';
}

function valueTitle(label: string, value: number): string {
  if (value >= 5) return `${label} ${fmt.signed(value, 1)} — clearly above replacement`;
  if (value <= -5) return `${label} ${fmt.signed(value, 1)} — below replacement`;
  return `${label} ${fmt.signed(value, 1)} — near replacement`;
}

function rowTierBand(tier: number | null): number | null {
  return tier === null ? null : Math.min(Math.max(tier, 1), 9);
}

function findByeConflict(
  player: Player,
  roster: PlayerId[],
  universe: NonNullable<AppState['universe']>,
): BoardRow['byeConflict'] {
  if (!player.position || player.bye_week === null) return null;

  const playerNames = roster
    .filter((id) => id !== player.player_id_sleeper)
    .map((id) => universe.get(id))
    .filter((rostered): rostered is Player =>
      Boolean(rostered && rostered.position === player.position && rostered.bye_week === player.bye_week),
    )
    .map((rostered) => rostered.full_name);

  return playerNames.length > 0 ? { week: player.bye_week, playerNames } : null;
}

function signalFromDelta(delta: number | null, threshold: number): BoardSignal | null {
  if (delta === null) return null;
  if (delta >= threshold) return 'good';
  if (delta <= -threshold) return 'bad';
  return 'neutral';
}

function signalFromValue(value: number | null): BoardSignal | null {
  if (value === null) return null;
  if (value >= 5) return 'good';
  if (value <= -5) return 'bad';
  return 'neutral';
}

function signalClass(signal: BoardSignal | null): string {
  return signal ? `signal-${signal}` : '';
}

export interface TierAlertPlayer {
  player_key: PlayerId;
  name: string;
  tier: number;
  projected_points: number | null;
  availability_probability: number | null;
}

/**
 * Surface tier cliffs the user is about to fall off.
 *
 * Only positions the user still *needs* raise an alert. A warning that the
 * QB tier is thinning is noise to somebody who already has two quarterbacks,
 * and a board that cries wolf every poll gets ignored by round four.
 */
export function tierAlerts(state: AppState, k = 3): TierAlert[] {
  const { universe, draft, blend } = state;
  if (!universe || !draft || !blend) return [];

  const active = activeTierModel(state);
  if (!active) return [];

  const drafted = new Set(Object.values(draft.all_rosters).flat());
  const availableByPosition = new Map<Position, TierAlertPlayer[]>();
  for (const id of active.order) {
    if (drafted.has(id)) continue;
    const player = universe.get(id);
    const tier = active.tiers.get(id);
    if (!player?.position || tier === null || tier === undefined) continue;
    const availability = active.availability(id);
    const list = availableByPosition.get(player.position) ?? [];
    list.push({
      player_key: id,
      name: player.full_name,
      tier,
      projected_points: active.projectedPoints(id),
      availability_probability: availability.known ? availability.probability : null,
    });
    availableByPosition.set(player.position, list);
  }

  const out: TierAlert[] = [];

  for (const position of FANTASY_POSITIONS) {
    const need = active.need.score[position] ?? 0;
    if (need < 0.5) continue;

    const positionPool = availableByPosition.get(position) ?? [];
    const tier = Math.min(...positionPool.map((player) => player.tier));
    const currentPlayers = positionPool.filter((player) => player.tier === tier);
    const remaining = currentPlayers.length;
    if (remaining === 0 || remaining > k) continue;

    // A whole-tier warning is only useful when the model can make the claim
    // for every player in the tier. Unknown ADP is not evidence that a player
    // will disappear, so suppress the card instead of guessing.
    if (active.targetPick === null || currentPlayers.some((player) => player.availability_probability === null)) continue;
    const bestSurvivor = [...currentPlayers].sort(
      (a, b) => b.availability_probability! - a.availability_probability!,
    )[0]!;
    if (bestSurvivor.availability_probability! >= 0.5) continue;

    const boundaryPlayer = [...currentPlayers]
      .filter((player) => player.projected_points !== null)
      .sort((a, b) => a.projected_points! - b.projected_points!)[0] ?? currentPlayers[currentPlayers.length - 1]!;
    const nextTier = positionPool
      .filter((player) => player.tier > tier)
      .reduce<number | null>((lowest, player) => lowest === null ? player.tier : Math.min(lowest, player.tier), null);
    const nextPlayer = nextTier === null
      ? null
      : bestProjectedPlayer(positionPool.filter((player) => player.tier === nextTier));
    const projectedPointDelta =
      boundaryPlayer.projected_points !== null && nextPlayer !== null && nextPlayer.projected_points !== null
        ? boundaryPlayer.projected_points - nextPlayer.projected_points
        : null;

    out.push({
      position,
      remaining,
      tier,
      urgent: remaining === 1,
      target_pick_no: active.targetPick,
      best_survival_probability: bestSurvivor.availability_probability!,
      current_players: currentPlayers,
      next_player: nextPlayer,
      projected_point_delta: projectedPointDelta,
      message: tierAlertMessage(
        position,
        tier,
        currentPlayers,
        bestSurvivor,
        active.targetPick,
        nextPlayer,
        boundaryPlayer,
        projectedPointDelta,
      ),
    });
  }

  return out.sort((a, b) => a.remaining - b.remaining);
}

function bestProjectedPlayer(players: TierAlertPlayer[]): TierAlertPlayer | null {
  return [...players].sort((a, b) => {
    if (a.projected_points === null && b.projected_points === null) return 0;
    if (a.projected_points === null) return 1;
    if (b.projected_points === null) return -1;
    return b.projected_points - a.projected_points;
  })[0] ?? null;
}

function tierAlertMessage(
  position: Position,
  tier: number,
  currentPlayers: TierAlertPlayer[],
  bestSurvivor: TierAlertPlayer,
  targetPick: number,
  nextPlayer: TierAlertPlayer | null,
  boundaryPlayer: TierAlertPlayer,
  projectedPointDelta: number | null,
): string {
  const current =
    currentPlayers.length === 1
      ? `${currentPlayers[0]!.name} is the last option${projectedPointsText(currentPlayers[0]!.projected_points)} with a ${Math.round(bestSurvivor.availability_probability! * 100)}% chance to reach pick #${targetPick}`
      : `${currentPlayers.map((player) => player.name).join(', ')} remain; ${bestSurvivor.name} has the best chance to reach pick #${targetPick} at ${Math.round(bestSurvivor.availability_probability! * 100)}%. Last option by projection: ${boundaryPlayer.name}${projectedPointsText(boundaryPlayer.projected_points)}`;
  const next = nextPlayer
    ? ` Next: ${nextPlayer.name} (tier ${nextPlayer.tier}${nextPlayer.projected_points === null ? '' : `, ${nextPlayer.projected_points.toFixed(1)} projected points`}). ${pointDropText(projectedPointDelta, boundaryPlayer.name)}`
    : ' No later tiered option is currently ranked.';
  return `${position} tier ${tier} cliff: ${current}.${next}`;
}

function projectedPointsText(points: number | null): string {
  return points === null ? '' : ` (${points.toFixed(1)} projected points)`;
}

function pointDropText(delta: number | null, boundaryName: string): string {
  if (delta === null) return `Projected drop from ${boundaryName} unavailable.`;
  if (Math.abs(delta) < 0.05) return `Drop from ${boundaryName}: about 0.0 projected points.`;
  return `Drop from ${boundaryName}: ${Math.abs(delta).toFixed(1)} projected points.`;
}

function activeTierModel(state: AppState): {
  need: NonNullable<AppState['engine']>['need'];
  tiers: Map<PlayerId, number>;
  order: PlayerId[];
  targetPick: number | null;
  projectedPoints: (id: PlayerId) => number | null;
  availability: (id: PlayerId) => { probability: number; known: boolean };
} | null {
  if (state.config.active_source_id === 'blueprint' && state.blueprint) {
    return {
      need: state.blueprint.need,
      tiers: state.blueprint.tiers,
      order: state.blueprint.order,
      targetPick: state.blueprint.my_next_pick,
      projectedPoints: (id) => state.blueprint?.valuations.get(id)?.projected_points ?? null,
      availability: (id) => {
        const valuation = state.blueprint?.valuations.get(id);
        return {
          probability: valuation?.probability_available_next_pick ?? 0,
          known: valuation?.availability_known ?? false,
        };
      },
    };
  }
  if (!state.engine) return null;
  return {
    need: state.engine.need,
    tiers: state.engine.tiers,
    order: state.blend?.order ?? [],
    targetPick: state.engine.my_next_pick,
    projectedPoints: (id) => state.engine?.valuations.get(id)?.proj_points ?? null,
    availability: (id) => {
      const valuation = state.engine?.valuations.get(id);
      return {
        probability: valuation?.availability_prob ?? 0,
        known: valuation?.availability_known ?? false,
      };
    },
  };
}
